import { Plane, Brush, Video } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { Button, SectionBreakCurve, Dropdown } from '@/components';
import { env } from '@/config/env';
import {
  CalendarItemSkeleton,
  CalendarEmptyState,
  LessonsList,
} from '@/features/calendar/components';
import { usePageView } from '@/lib/analytics';
import { useIsMobile } from '@/lib/hooks';
import { getSessionLabel } from '@/lib/utils';
import { useSchedulePageStore } from '@/stores/schedule-page';

import { useScheduleData, useParentConnectSessionsData } from './api/queries';
import { mapParentConnectToLessons } from './utils/map-parent-connect';

const SESSION_LABEL = getSessionLabel();

export default function Calendar() {
  usePageView('Schedule');
  const isMobile = useIsMobile();

  const {
    selectedMonth,
    setSelectedMonth,
    filter,
    setFilter,
    resetFilters,
    isOpen,
  } = useSchedulePageStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { lessons, pastLessons, isLoading, timezone } = useScheduleData();
  const { sessions: parentConnectRows } = useParentConnectSessionsData();

  // Parent-connect (TPC) rows come from a separate endpoint; merge them
  // into the unified list so they show under "Select all" and filter
  // under the "Parent Connect / PTM" chip.
  const allLessons = useMemo(
    () => [
      ...lessons,
      ...mapParentConnectToLessons(parentConnectRows, timezone),
    ],
    [lessons, parentConnectRows, timezone]
  );

  const filteredLessons = useMemo(() => {
    let result = [...allLessons];

    if (filter?.status) {
      result = result.filter(l => l.status === filter.status);
    } else if (filter?.variant) {
      result = result.filter(l => l.variant === filter.variant);
    }

    result.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time ?? '00:00'}`);
      const dateB = new Date(`${b.date} ${b.time ?? '00:00'}`);
      return dateA.getTime() - dateB.getTime();
    });

    return result;
  }, [allLessons, filter]);

  const dropdownOptions =
    allLessons.length > 0
      ? (() => {
          const monthYears = new Set<string>();
          for (const lesson of allLessons) {
            const lessonDate = new Date(lesson.date);
            const monthYear = lessonDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            });
            monthYears.add(monthYear);
          }
          const monthOptions = [...monthYears]
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map(monthYear => ({ label: monthYear, value: monthYear }));

          return [
            { label: 'Select all', value: 'Select all' },
            ...monthOptions,
          ];
        })()
      : [{ label: 'Select all', value: 'Select all' }];

  useLayoutEffect(() => {
    if (scrollContainerRef.current && pastLessons.length > 0) {
      const targetIndex = Math.max(0, pastLessons.length - 2);

      setTimeout(() => {
        if (scrollContainerRef.current) {
          const targetElement = scrollContainerRef.current.querySelector(
            `[data-calendar-index="${targetIndex}"]`
          );

          if (targetElement) {
            const containerRect =
              scrollContainerRef.current.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();
            const scrollOffset = targetRect.top - containerRect.top;

            scrollContainerRef.current.scrollTop =
              scrollContainerRef.current.scrollTop + scrollOffset;
          }
        }
      }, 100);
    }
  }, [pastLessons]);

  useEffect(() => {
    if (!isOpen) {
      resetFilters();
    }
  }, [isOpen, resetFilters]);

  const filterKey = `${filter?.status ?? ''}-${filter?.variant ?? ''}`;

  return (
    <div className="w-full flex flex-col">
      {/* Header */}
      <div className="flex items-left justify-between flex-col lg:flex-row">
        <h1 className="text-2xl font-bold">All {SESSION_LABEL.capitalize}s</h1>
        {timezone ? (
          <p className="text-sm text-magical-moonlight-d1">
            All mentioned times are in&nbsp;
            <span className="text-magical-moonlight-d2">{timezone}</span>
          </p>
        ) : null}
      </div>

      {/* Divider */}
      <SectionBreakCurve
        bottomColor="transparent"
        strokeColor="var(--color-magical-moonlight-d1)"
        strokeWidth={1}
        className="my-3 lg:my-6"
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-start gap-4">
        <div>
          <Dropdown
            size={isMobile ? 'sm' : 'default'}
            options={dropdownOptions}
            value={selectedMonth}
            onValueChange={setSelectedMonth}
          />
        </div>

        <Button
          variant={filter?.status === 'holiday' ? 'default' : 'outline'}
          size={isMobile ? 'sm' : 'default'}
          icon={<Plane />}
          onClick={() => setFilter({ status: 'holiday' })}
        >
          Holiday
        </Button>

        {/* Hobby Class and Parent Connect are online-only surfaces: the
            offline (learning-centre) backend serves neither, so both chips
            would filter to an always-empty list. */}
        {env.IS_OFFLINE ? null : (
          <>
            <Button
              variant={
                filter?.variant === 'hobby_class' ? 'default' : 'outline'
              }
              icon={<Brush />}
              onClick={() => setFilter({ variant: 'hobby_class' })}
              size={isMobile ? 'sm' : 'default'}
            >
              Hobby Class
            </Button>

            <Button
              variant={
                filter?.variant === 'parent_connect' ? 'default' : 'outline'
              }
              icon={<Video />}
              onClick={() => setFilter({ variant: 'parent_connect' })}
              size={isMobile ? 'sm' : 'default'}
            >
              Parent Connect / PTM
            </Button>
          </>
        )}
      </div>

      {/* Calendar List */}
      <div className="flex-1 mt-3 lg:mt-6">
        <div
          ref={scrollContainerRef}
          className="h-[600px] overflow-x-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-magical-moonlight-d1 scrollbar-track-transparent"
        >
          <div key={filterKey} className="space-y-3 mb-48">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CalendarItemSkeleton key={index} />
                ))}
              </div>
            ) : (
              (() => {
                const monthMatches =
                  selectedMonth === 'Select all' ||
                  filteredLessons.some(
                    lesson =>
                      new Date(lesson.date).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      }) === selectedMonth
                  );

                const hasActiveFilters =
                  !!filter?.status ||
                  !!filter?.variant ||
                  selectedMonth !== 'Select all';

                if (
                  allLessons.length === 0 ||
                  (!monthMatches && hasActiveFilters) ||
                  filteredLessons.length === 0
                ) {
                  return (
                    <CalendarEmptyState
                      filter={filter}
                      selectedMonth={selectedMonth}
                    />
                  );
                }

                return (
                  <LessonsList
                    lessons={filteredLessons}
                    selectedMonth={selectedMonth}
                  />
                );
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
